import { useEffect, useRef, useState, type ReactNode } from 'react';
import { IconChevron } from './icons';

export interface MenuItem {
  readonly label: string;
  readonly hint?: string;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  /** Draws a divider above this item. */
  readonly separated?: boolean;
}

interface Props {
  readonly label: string;
  readonly items: readonly MenuItem[];
  readonly icon?: ReactNode;
}

/**
 * One button instead of a row of five. Closes on Escape, on outside click, and
 * on selection; arrow keys walk the items.
 */
export function Menu({ label, items, icon }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        setOpen(false);
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const buttons = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('button:not([disabled])') ?? [])];
      if (buttons.length === 0) return;
      event.preventDefault();
      const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
      const next = event.key === 'ArrowDown' ? index + 1 : index - 1;
      buttons[(next + buttons.length) % buttons.length]?.focus();
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <div className="menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className="btn btn-quiet"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((v) => !v)}
      >
        {icon}
        {label}
        <span className="chev" data-open={open}>
          <IconChevron />
        </span>
      </button>
      {open ? (
        <div className="menu" role="menu" aria-label={label} ref={listRef}>
          {items.map((item) => (
            <div key={item.label} role="none" className={item.separated ? 'sep' : undefined}>
              <button
                type="button"
                role="menuitem"
                disabled={item.disabled}
                // The hint is decoration: keeping it out of the accessible name
                // means the item is still called "SVG", not "SVG Cmd+Shift+S".
                aria-keyshortcuts={item.hint}
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                <span>{item.label}</span>
                {item.hint ? <kbd aria-hidden="true">{item.hint}</kbd> : null}
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
